import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { InfoOutlined as InfoOutlinedIcon } from "@material-ui/icons";
import { Application, LandingZoneRole } from "@store";
import * as Api from "@api";
import { Environment, InfrastructureApplication, Role } from "@api";
import { Access } from "../../../../../context/state/types";
import Hooks from "@hooks";

interface Props {
  application: Application;
  access: Access;
  accesses: Access[];
  onError: () => void;
  onUpdateAccess: (acccess: any) => void;
  onCancel: () => void;
}

type Values = {
  environment?: Environment;
  subCategoryName?: string;
  role?: Role;
  infrastructureApplication?: InfrastructureApplication;
  accessDetails?: string;
};

export const Form: React.FC<Props> = (props) => {
  const { application, access, accesses } = props;
  const initial_values = {
    environment: access.environment,
    subCategoryName: access.subCategoryName,
    role: access.role,
    infrastructureApplication: access.infrastructureApplication,
    accessDetails: access.accessDetails,
  };
  const [environments, setEnvironments] = React.useState<Environment[]>([]);
  const [roles, setRoles] = React.useState<Role[]>([]);
  const [
    infrastructureApplications,
    setInfrastructureApplications,
  ] = React.useState<InfrastructureApplication[]>([]);
  const [values, setValues] = React.useState<Values>(initial_values);
  const [formErrors, setFormErrors] = React.useState<string[]>([]);
  const lzRoles = Hooks.useLandingZoneRoles();

  React.useEffect(() => {
    Api.getEnvironments(application.manual ? undefined : application.id).then(
      (environments) => {
        setEnvironments(environments);
      }
    );
  }, [application]);

  React.useEffect(() => {
    if (!application.manual && values.environment) {
      Api.getRoles(application.id, values.environment.name).then((roles) => {
        setRoles(roles);
      });
    }
  }, [application, values.environment]);

  React.useEffect(() => {
    if (application.manual) {
      Api.getInfrastructureApplications(application.id).then(
        (infrastructureApplications) => {
          setInfrastructureApplications(infrastructureApplications);
        }
      );
    }
  }, [application]);

  function onEnvironmentChange(event: React.ChangeEvent<any>): void {
    const value = environments.find(
      (environment) => environment.id === event.target.value
    );

    setValues((values) => ({
      ...values,
      environment: value,
    }));
    if (value) {
      setFormErrors(formErrors.filter((error) => error !== "environment"));
    } else if (!formErrors.includes("environment")) {
      setFormErrors(formErrors.concat("environment"));
    }
  }

  function onSubCategoryNameChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const value = event.target.value;

    setValues((values) => ({
      ...values,
      subCategoryName: value,
    }));
    if (value) {
      setFormErrors(formErrors.filter((error) => error !== "subCategoryName"));
    } else if (!formErrors.includes("subCategoryName")) {
      setFormErrors(formErrors.concat("subCategoryName"));
    }
  }

  function onRoleChange(event: React.ChangeEvent<any>): void {
    const value = event.target.value;

    setValues((values) => ({
      ...values,
      role:
        typeof value === "number"
          ? roles.find((role) => role.id === value)
          : {
              id: Number.NaN,
              name: value,
            },
    }));
    if (value !== undefined && value !== "") {
      setFormErrors(formErrors.filter((error) => error !== "role"));
    } else if (!formErrors.includes("role")) {
      setFormErrors(formErrors.concat("role"));
    }
  }

  function mapToRole(landingZoneRole: LandingZoneRole): Api.Role {
    return {
      id: landingZoneRole.id,
      name: landingZoneRole.landingZoneRoleName,
      description: "",
    };
  }

  function onLandingZoneRoleChange(event: React.ChangeEvent<any>): void {
    const value = event.target.value;

    const lzr = (lzRoles.value || []).find(
      ({ id }) => id === value
    ) as LandingZoneRole;

    setValues((values) => ({
      ...values,
      role: mapToRole(lzr),
    }));

    if (value !== undefined && value !== "") {
      setFormErrors(formErrors.filter((error) => error !== "role"));
    } else if (!formErrors.includes("role")) {
      setFormErrors(formErrors.concat("role"));
    }
  }

  function onInfrastructureApplicationChange(
    event: React.ChangeEvent<any>
  ): void {
    const value = infrastructureApplications.find(
      (infrastructureApplication) =>
        infrastructureApplication.id === event.target.value
    );

    setValues((values) => ({
      ...values,
      infrastructureApplication: value,
    }));
    if (value) {
      setFormErrors(
        formErrors.filter((error) => error !== "infrastructureApplication")
      );
    } else if (!formErrors.includes("infrastructureApplication")) {
      setFormErrors(formErrors.concat("infrastructureApplication"));
    }
  }

  function onAccessDetailsChange(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const value = event.target.value;

    setValues((values) => ({
      ...values,
      accessDetails: value,
    }));
    if (value) {
      setFormErrors(formErrors.filter((error) => error !== "accessDetails"));
    } else if (!formErrors.includes("accessDetails")) {
      setFormErrors(formErrors.concat("accessDetails"));
    }
  }

  function validate(): Promise<boolean> {
    let errors = [];

    if (!values.environment && !application.lz) {
      errors.push("environment");
    }
    if (!values.role) {
      errors.push("role");
    }
    if (!values.accessDetails) {
      errors.push("accessDetails");
    }

    if (application?.manual && !values.subCategoryName) {
      errors.push("subCategoryName");
    }
    // if (application?.manual && !values.infrastructureApplication) {
    //   errors.push("infrastructureApplication");
    // }

    setFormErrors(errors);

    return Promise.resolve(!errors.length);
  }

  const [isDuplicateRecord, setIsDuplicateRecord] = useState(false);

  useEffect(() => {
    const { environment, role } = values;

    if (
      environment?.id !== access.environment?.id ||
      role?.id !== access.role?.id
    ) {
      const filterData = accesses.filter(
        (d) => d.environment?.id === environment?.id && d.role?.id === role?.id
      );
      if (filterData.length !== 0) {
        console.log("duplicate");
        setIsDuplicateRecord(true);
      } else {
        setIsDuplicateRecord(false);
      }
    } else {
      setIsDuplicateRecord(false);
    }
  }, [values.environment, values.role]);

  function onSave(event: React.MouseEvent<HTMLButtonElement>): void {
    setIsDuplicateRecord(false);
    const {
      environment,
      subCategoryName,
      role,
      infrastructureApplication,
      accessDetails,
    } = values;

    validate().then((valid) => {
      let validEnv: boolean = application.lz
        ? true
        : environment
        ? true
        : false;
      if (valid && validEnv && role && accessDetails) {
        props.onUpdateAccess(
          Object.assign(
            {
              environment,
              role,
              accessDetails,
              classification: application?.categoryType
                ? application.categoryType
                : "",
            },
            application?.manual
              ? {
                  subCategoryName,
                  infrastructureApplication,
                }
              : {}
          )
        );
      } else {
        props.onError();
      }
    });
  }

  const duplicateErrorMessage = isDuplicateRecord ? (
    <p style={{ color: "#FF0000" }}>Duplicate data found.</p>
  ) : null;
  return (
    <Box component="form" pr={2}>
      <Box>
        <FormControl fullWidth error={formErrors.includes("environment")}>
          <FormLabel component={Box} display="flex" alignItems="center">
            <Typography variant="caption" noWrap>
              Application / Infrastructure / Domain Account / LZ
            </Typography>
          </FormLabel>
          {duplicateErrorMessage}
          <Input
            inputProps={{
              maxLength: 200,
            }}
            name="appName"
            onChange={onRoleChange}
            required
            disabled={true}
            value={application?.name}
          />
        </FormControl>
      </Box>
      <Box mt={2}>
        <FormControl fullWidth error={formErrors.includes("environment")}>
          <FormLabel component={Box} display="flex" alignItems="center">
            <Typography variant="caption" noWrap>
              Domain Host
            </Typography>
          </FormLabel>
          <Input
            inputProps={{
              maxLength: 200,
            }}
            name="hostName"
            onChange={onRoleChange}
            required
            disabled={true}
            value={application?.host}
          />
        </FormControl>
      </Box>
      {application.lz ? null : (
        <Box mt={2}>
          <FormControl fullWidth error={formErrors.includes("environment")}>
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Environment
              </Typography>
              <Tooltip
                arrow
                title="This pertains to where the selectedSystem will run. (Ex. PROD, DEV etc.)"
              >
                <InfoOutlinedIcon color="primary" fontSize="small" />
              </Tooltip>
            </FormLabel>
            <Select
              disabled={!environments.length}
              displayEmpty
              name="environment"
              onChange={onEnvironmentChange}
              renderValue={(value) => (
                <Box color={`text.${value ? "primary" : "disabled"}`}>
                  {`${
                    value
                      ? environments.find(
                          (environment) => environment.id === value
                        )?.name
                      : "No Selection"
                  }`}
                </Box>
              )}
              value={values.environment?.id || ""}
            >
              {environments.map((environment) => (
                <MenuItem key={environment.id} value={environment.id}>
                  {environment.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {application?.manual ? (
        <Box mt={2}>
          <FormControl fullWidth error={formErrors.includes("subCategoryName")}>
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Device / Server / Database / Network Name
              </Typography>
            </FormLabel>
            <Input
              inputProps={{
                maxLength: 200,
              }}
              name="subCategoryName"
              onChange={onSubCategoryNameChange}
              required
              value={values.subCategoryName || ""}
            />
            <FormHelperText></FormHelperText>
          </FormControl>
        </Box>
      ) : null}
      {application?.manual ? (
        <Box mt={2}>
          <FormControl fullWidth error={formErrors.includes("role")}>
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Role (e.g. Admin, User, Read/Write etc.)
              </Typography>
            </FormLabel>
            <Input
              inputProps={{
                maxLength: 200,
              }}
              name="role"
              onChange={onRoleChange}
              required
              value={values.role?.name || ""}
            />
            <FormHelperText></FormHelperText>
          </FormControl>
        </Box>
      ) : application.lz ? (
        <Box mt={2}>
          <FormControl fullWidth error={formErrors.includes("role")}>
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Role
              </Typography>
              <Tooltip arrow title="Role for landing zone request.">
                <InfoOutlinedIcon
                  color="primary"
                  fontSize="small"
                  style={{ marginTop: "-1px" }}
                />
              </Tooltip>
            </FormLabel>
            <Select
              disabled={!lzRoles.value?.length}
              displayEmpty
              name="role"
              onChange={onLandingZoneRoleChange}
              renderValue={(value) => (
                <Box color={`text.${value ? "primary" : "disabled"}`}>
                  {`${
                    value
                      ? lzRoles.value?.find((lzr) => lzr.id === value)
                          ?.landingZoneRoleName
                      : "No Selection"
                  }`}
                </Box>
              )}
              value={values.role?.id || ""}
            >
              {lzRoles.value?.map((lzr) => (
                <MenuItem key={lzr.id} value={lzr.id}>
                  {lzr.landingZoneRoleName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : (
        <Box mt={2}>
          <FormControl fullWidth error={formErrors.includes("role")}>
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Role
              </Typography>
              <Tooltip
                arrow
                title="It is a level of access (e.g. Admin, Supv, etc.) for the requested selectedSystem."
              >
                <InfoOutlinedIcon
                  color="primary"
                  fontSize="small"
                  style={{ marginTop: "-1px" }}
                />
              </Tooltip>
            </FormLabel>
            <Select
              disabled={!roles.length}
              displayEmpty
              name="role"
              onChange={onRoleChange}
              renderValue={(value) => (
                <Box color={`text.${value ? "primary" : "disabled"}`}>
                  {`${
                    value
                      ? roles.find((role) => role.id === value)?.name
                      : "No Selection"
                  }`}
                </Box>
              )}
              value={values.role?.id || ""}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      {application?.manual && infrastructureApplications.length ? (
        <Box mt={2}>
          <FormControl
            fullWidth
            error={formErrors.includes("infrastructureApplication")}
          >
            <FormLabel component={Box} display="flex" alignItems="center">
              <Typography variant="caption" noWrap>
                Infrastructure Application
              </Typography>
            </FormLabel>
            <Select
              disabled={!infrastructureApplications.length}
              displayEmpty
              name="infrastructureApplication"
              onChange={onInfrastructureApplicationChange}
              renderValue={(value) => (
                <Box color={`text.${value ? "primary" : "disabled"}`}>
                  {`${
                    value
                      ? infrastructureApplications.find(
                          (infrastructureApplication) =>
                            infrastructureApplication.id === value
                        )?.name
                      : "No Selection"
                  }`}
                </Box>
              )}
              value={values.infrastructureApplication?.id || ""}
            >
              {infrastructureApplications.map((infrastructureApplication) => (
                <MenuItem
                  key={infrastructureApplication.id}
                  value={infrastructureApplication.id}
                >
                  {infrastructureApplication.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : null}
      <Box mt={2}>
        <FormControl fullWidth error={formErrors.includes("accessDetails")}>
          <FormLabel component={Box} display="flex" alignItems="center">
            <Typography variant="caption" noWrap>
              Access Details
            </Typography>
          </FormLabel>
          {application?.uamNote ? (
            <Box my={1} pl={1} borderLeft={4} borderColor="warning.main">
              <Typography variant="caption">{application.uamNote}</Typography>
            </Box>
          ) : null}
          <Input
            inputProps={{
              maxLength: 1000,
            }}
            name="accessDetails"
            multiline
            onChange={onAccessDetailsChange}
            required
            rowsMax={5}
            placeholder={application?.accessDetailNote || "Write details here"}
            value={values.accessDetails || ""}
          />
          <FormHelperText></FormHelperText>
        </FormControl>
      </Box>
      <Box mt={2} display="flex" justifyContent="flex-end">
        <Button variant="outlined" color="primary" onClick={props.onCancel}>
          cancel
        </Button>
        &nbsp;&nbsp;&nbsp;
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          disabled={isDuplicateRecord}
        >
          save
        </Button>
      </Box>
    </Box>
  );
};
